import React from 'react';

const PropertyManagement = ({ language = 'en' }) => {
  const content = {
    en: {
      title: 'Automated Management Fee Collection',
      subtitle: 'Property Management Fee System',
      intro: 'We provide a dedicated automatic management fee collection system for residential buildings, communities and offices, integrating billing, payment, notification and financial management for transparent finance and efficient administration. Residents can pay management fees online via the electronic screen in the lobby.',
      services: {
        title: 'Services',
        items: [
          'Automatic calculation and invoice generation for management fees',
          'Multiple payment methods: credit card, ATM, mobile payment',
          'Automatic payment reminders and overdue notifications',
          'Real-time payment records and financial report inquiry',
          'Resident account authority and online query management'
        ]
      },
      advantages: {
        title: 'Advantages',
        items: [
          'Automated process, reduce manual workload',
          'Convenient payment, improve collection rate',
          'Clear accounting, transparent and traceable finance',
          'Secure data, stable and easy to use'
        ]
      },
      process: {
        title: 'Service Process',
        steps: 'System Setup → Resident Data Filing → Billing Configuration → Launch & Use → Backend Maintenance'
      }
    },
    zh: {
      title: '自動化管理費收費方案',
      subtitle: '管理費收費系統',
      intro: '提供社區、大樓、大廈專用管理費自動收費系統，整合繳費、通知、帳務與查詢功能，實現財務透明、管理輕鬆、作業自動化。住户可在電子屏幕上，透過線上支付管理費用。',
      services: {
        title: '服務項目',
        items: [
          '管理費自動計算與帳單產生',
          '多管道繳費：線上刷卡、ATM、行動支付',
          '繳費提醒、逾期通知自動推送',
          '繳費紀錄、帳務報表即時查詢',
          '住戶帳號權限管理與線上查詢'
        ]
      },
      advantages: {
        title: '系統優勢',
        items: [
          '自動化作業，減少人工負擔',
          '繳費方便，提升繳費率',
          '帳務清晰，財務透明可追溯',
          '資料安全，穩定好用'
        ]
      },
      process: {
        title: '服務流程',
        steps: '系統建置 → 住戶資料建檔 → 帳單設定 → 上線使用 → 後台維護'
      }
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <section id="property-management" className="service__section pt-120 pb-120">
      <div className="container">
        <div className="project__head text-center mb-60">
          <span className="common__sub aos-init aos-animate" data-aos="fade-down" data-aos-duration="1000">
            {currentContent.title}
          </span>
          <h2 className="fw-500 aos-init aos-animate" data-aos="fade-up" data-aos-duration="1200" style={{ color: '#c9f31d' }}>
            {currentContent.subtitle}
          </h2>
        </div>

        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Hero Image */}
          <div style={{ position: 'relative', zIndex: 1, borderRadius: '12px', overflow: 'hidden', marginBottom: '2rem' }}>
            <img
              src="/assets/photos/property-management.jpeg"
              alt="Property Management Fee System"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '12px' }}
            />
          </div>
          {/* Intro */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <p className="pra fz-18" style={{
              lineHeight: '1.9',
              color: '#e0e0e0',
              textAlign: 'justify',
              margin: 0
            }}>
              {currentContent.intro}
            </p>
          </div>

          {/* Services */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="200"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.services.title}
            </h3>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {currentContent.services.items.map((item, idx) => (
                <li key={idx} className="pra fz-16 mb-2" style={{ 
                  paddingLeft: '1.5rem',
                  position: 'relative',
                  lineHeight: '1.7',
                  color: '#e0e0e0'
                }}>
                  <span style={{ 
                    position: 'absolute',
                    left: '0',
                    color: '#c9f31d',
                    fontWeight: 'bold'
                  }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Advantages */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="400"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.advantages.title}
            </h3>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {currentContent.advantages.items.map((item, idx) => (
                <li key={idx} className="pra fz-16 mb-2" style={{ 
                  paddingLeft: '1.5rem',
                  position: 'relative',
                  lineHeight: '1.7',
                  color: '#e0e0e0'
                }}>
                  <span style={{ 
                    position: 'absolute',
                    left: '0',
                    color: '#c9f31d',
                    fontWeight: 'bold'
                  }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Process */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="600"
            style={{
              background: 'linear-gradient(135deg, rgba(48, 102, 255, 0.1) 0%, rgba(201, 243, 29, 0.1) 100%)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(201, 243, 29, 0.2)',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.process.title}
            </h3>
            <p className="pra fz-18" style={{
              lineHeight: '1.9',
              color: '#c9f31d',
              textAlign: 'center',
              margin: 0,
              fontWeight: '500'
            }}>
              {currentContent.process.steps}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertyManagement;
